import * as React from "react";

import { canAnimate } from "@/lib/motion";

const DEFAULT_VISIBLE_RATIO = 0.4;

/**
 * Flips to true the first time the element is at least `visibleRatio` on screen, then
 * stops watching. Reports true from the start when motion is off or `IntersectionObserver`
 * is missing, so callers render their final state instead of waiting for a reveal.
 */
export function useInViewOnce<T extends Element>(
  visibleRatio = DEFAULT_VISIBLE_RATIO,
): [React.RefCallback<T>, boolean] {
  const [isInView, setIsInView] = React.useState(
    () => !canAnimate() || typeof IntersectionObserver === "undefined",
  );
  const [element, setElement] = React.useState<T | null>(null);

  React.useEffect(() => {
    if (isInView || !element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: visibleRatio },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, isInView, visibleRatio]);

  return [setElement, isInView];
}
